import React, { useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { JointFillPolygon, JointPolygonData } from '../../types/floorplannerTypes';
import { useThree } from '@react-three/fiber';
import { LineGeometry, LineMaterial, LineSegments2 } from 'three-stdlib';
import { renderStore } from '../../store/renderStore';
import { observer } from 'mobx-react-lite';
import PatternFillMesh from './PatternFillMesh';
import { resolveFillOpacity } from '../../utils/fillOpacity';

interface JointPolygonProps {
  jointPolygon: JointPolygonData;
}

/**
 * Renders a joint polygon: gap-fill islands plus the closing outline that
 * bridges the missing wall boundary. The fill is rendered as one mesh per island
 * (each may have holes), and it must not overlap a wall footprint.
 */
export const getRenderableJointFillPolygons = (jointPolygon: JointPolygonData): JointFillPolygon[] =>
  jointPolygon.fillPolygons ?? [];

const JointPolygon: React.FC<JointPolygonProps> = observer(({ jointPolygon }) => {
  const { size } = useThree();

  // Keep the joint visually attached while all participating walls translate/rotate.
  const isTranslating = jointPolygon.wallIds.every(id => renderStore.isWallTranslating(id));
  const translateOffset = isTranslating ? renderStore.groupTranslateOffset : { x: 0, y: 0 };

  const isRotating = jointPolygon.wallIds.every(id => renderStore.isWallRotating(id));
  const rotationData = isRotating
    ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
    : null;

  let finalPosition = [translateOffset.x, translateOffset.y] as [number, number];
  let groupRotation = [0, 0, 0] as [number, number, number];
  if (rotationData) {
    const { angle, center } = rotationData;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotationCompX = center.x * (1 - cos) + center.y * sin;
    const rotationCompY = center.y * (1 - cos) - center.x * sin;
    finalPosition = [translateOffset.x + rotationCompX, translateOffset.y + rotationCompY];
    groupRotation = [0, 0, angle];
  }

  const fillZ = jointPolygon.zIndex;
  const outlineZ = 0.003;
  const fillOpacity = resolveFillOpacity(jointPolygon);
  const fillIsTransparent = fillOpacity < 1;

  // Build one THREE.Shape per gap-fill island (with holes). Falls back to
  // gap-only fill polygons, then the legacy single-ring `vertices`.
  const shapes = useMemo(() => {
    const result: THREE.Shape[] = [];

    const buildShape = (outer: THREE.Vector2[], holes: THREE.Vector2[][] = []): THREE.Shape | null => {
      if (outer.length < 3) {
        return null;
      }
      const shape = new THREE.Shape();
      shape.moveTo(outer[0].x, outer[0].y);
      for (let i = 1; i < outer.length; i++) {
        shape.lineTo(outer[i].x, outer[i].y);
      }
      shape.closePath();
      for (const hole of holes) {
        if (hole.length < 3) continue;
        const path = new THREE.Path();
        path.moveTo(hole[0].x, hole[0].y);
        for (let i = 1; i < hole.length; i++) {
          path.lineTo(hole[i].x, hole[i].y);
        }
        path.closePath();
        shape.holes.push(path);
      }
      return shape;
    };

    const polygons = getRenderableJointFillPolygons(jointPolygon);

    if (polygons.length > 0) {
      for (const polygon of polygons) {
        const shape = buildShape(polygon.outer, polygon.holes);
        if (shape) {
          result.push(shape);
        }
      }
    } else if (jointPolygon.vertices.length >= 3) {
      const shape = buildShape(jointPolygon.vertices);
      if (shape) {
        result.push(shape);
      }
    }

    return result;
  }, [jointPolygon, jointPolygon.fillPolygons, jointPolygon.vertices]);

  // Closing outline segments (from the boolean boundary). Grouped by style for
  // efficient LineSegments2 rendering.
  const outlines = useMemo(() => {
    const sourceSegments = jointPolygon.outlineSegments ?? [];
    if (sourceSegments.length === 0) {
      return null;
    }

    const grouped = new Map<string, {
      color: number;
      width: number;
      dashed: boolean;
      positions: number[];
    }>();
    for (const segment of sourceSegments) {
      const direction = segment.end.clone().sub(segment.start);
      if (direction.length() < 1e-8) {
        continue;
      }
      const key = `${segment.color}|${segment.width}|${segment.dashed}`;
      const group = grouped.get(key) ?? {
        color: segment.color,
        width: segment.width,
        dashed: segment.dashed,
        positions: [],
      };
      group.positions.push(
        segment.start.x, segment.start.y, 0,
        segment.end.x, segment.end.y, 0
      );
      grouped.set(key, group);
    }

    return Array.from(grouped.values()).map(group => {
      const geometry = new LineGeometry();
      geometry.setPositions(group.positions);

      const material = new LineMaterial({
        color: group.color,
        linewidth: group.width,
        resolution: new THREE.Vector2(size.width, size.height),
      });

      if (group.dashed) {
        material.dashed = true;
        material.dashSize = 0.3;
        material.gapSize = 0.15;
      }
      material.needsUpdate = true;

      const segments = new LineSegments2(geometry, material);
      segments.position.set(0, 0, outlineZ);
      if (material.dashed) {
        segments.computeLineDistances();
      }

      return { geometry, material, segments };
    });
  }, [jointPolygon.outlineSegments, outlineZ, size.width, size.height]);

  useEffect(() => {
    return () => {
      outlines?.forEach(outline => {
        outline.geometry.dispose();
        outline.material.dispose();
      });
    };
  }, [outlines]);

  return (
    <group position={[finalPosition[0], finalPosition[1], 0]} rotation={groupRotation}>
      {shapes.map((shape, index) => (
        <mesh
          key={`joint-fill-${index}`}
          position={[0, 0, fillZ]}
          userData={{ type: 'jointPolygon', jointId: jointPolygon.id }}
        >
          <shapeGeometry args={[shape]} />
          {/* DoubleSide: joint fill islands are flat and small; DoubleSide avoids
              any winding-dependent invisibility for negligible cost. */}
          <PatternFillMesh
            fillColor={jointPolygon.fillColor}
            fillPattern={jointPolygon.fillPattern}
            useWorldSpace={true}
            side={THREE.DoubleSide}
            opacity={fillOpacity}
            transparent={fillIsTransparent}
            depthWrite={!fillIsTransparent}
          />
        </mesh>
      ))}
      {outlines && (
        <group position={[0, 0, 0]}>
          {outlines.map((outline, index) => (
            <primitive key={`joint-outline-${index}`} object={outline.segments} />
          ))}
        </group>
      )}
    </group>
  );
});

JointPolygon.displayName = 'JointPolygon';

export default JointPolygon;
